// src/hooks/usePortfolio.js
import { useState, useEffect, useCallback } from 'react';
import portfolioService from '../services/portfolio.service';
import authService from '../services/auth.service';

export const usePortfolio = () => {
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const userId = authService.getUserId();

  const fetchPortfolioSummary = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await portfolioService.getPortfolioSummary(userId);
      setSummary(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch portfolio summary');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchSettings = useCallback(async () => {
    if (!userId) return null;

    setLoading(true);
    setError(null);

    try {
      const data = await portfolioService.getUserSettings(userId);
      setSettings(data);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch settings');
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const updateSettings = useCallback(async (settingsData) => {
    if (!userId) return { success: false, error: 'User not authenticated' };

    setLoading(true);
    setError(null);

    try {
      const response = await portfolioService.updateUserSettings(userId, settingsData);
      setSettings(response);
      return { success: true, data: response };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update settings';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const updateYearlyGoal = useCallback(async (yearlyGoal) => {
    if (!userId) return { success: false, error: 'User not authenticated' };

    setLoading(true);
    setError(null);

    try {
      const response = await portfolioService.updateYearlyGoal(userId, yearlyGoal);
      setSettings(response);
      return { success: true, data: response };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update yearly goal';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    summary,
    settings,
    loading,
    error,
    fetchPortfolioSummary,
    fetchSettings,
    updateSettings,
    updateYearlyGoal
  };
};
